import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import Toast from "@/components/feature/Toast";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useSeo } from "@/hooks/useSeo";
import { useToast } from "@/hooks/useToast";
import { getVideoById, getCategoryName } from "@/utils/format";
import VideoPlayer from "./components/VideoPlayer";
import VideoInfo from "./components/VideoInfo";
import CommentSection from "./components/CommentSection";
import RelatedVideos from "./components/RelatedVideos";

export default function VideoPage() {
  const { id = "" } = useParams();
  const video = getVideoById(id);
  const { toast, showToast, hideToast } = useToast();
  const [likes, setLikes] = useLocalStorage<string[]>("gg_likes", []);
  const [favorites, setFavorites] = useLocalStorage<string[]>("gg_favorites", []);

  const firstSentence = video ? video.description.split("。")[0] : "";

  useSeo({
    title: video ? `${video.title} - 呱呱视频社区` : "视频 - 呱呱视频社区",
    description: video
      ? `${firstSentence}。${video.tags.join("、")}，尽在呱呱视频社区。`
      : "在呱呱视频社区观看精彩视频内容。",
    keywords: video ? `${video.tags.join(",")},${video.author},呱呱视频社区` : "呱呱视频社区",
  });

  if (!video) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12">
        <Breadcrumb items={[{ label: "视频未找到" }]} />
        <div className="mt-6">
          <EmptyState
            title="没有找到这个视频"
            description="视频可能已被删除或链接有误，回到首页发现更多精彩内容吧。"
            actionLabel="返回首页"
            onAction={() => window.REACT_APP_NAVIGATE?.("/")}
          />
        </div>
      </div>
    );
  }

  const liked = likes.includes(video.id);
  const favorited = favorites.includes(video.id);

  const toggleLike = () => {
    if (liked) {
      setLikes(likes.filter((x) => x !== video.id));
      showToast("已取消点赞", "info");
    } else {
      setLikes([...likes, video.id]);
      showToast("点赞成功");
    }
  };

  const toggleFavorite = () => {
    if (favorited) {
      setFavorites(favorites.filter((x) => x !== video.id));
      showToast("已取消收藏", "info");
    } else {
      setFavorites([...favorites, video.id]);
      showToast("已加入收藏");
    }
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，快去分享吧");
    } catch {
      showToast("分享链接：" + window.location.href, "info");
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
      <Breadcrumb
        items={[
          { label: getCategoryName(video.category), to: `/category/${video.category}` },
          { label: video.title },
        ]}
      />

      <div className="mt-5 flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="flex-1 min-w-0">
          <VideoPlayer cover={video.cover} title={video.title} duration={video.duration} />
          <VideoInfo
            title={video.title}
            author={video.author}
            authorType={video.authorType}
            category={video.category}
            views={video.views}
            publishDate={video.publishDate}
            description={video.description}
            tags={video.tags}
            liked={liked}
            favorited={favorited}
            onToggleLike={toggleLike}
            onToggleFavorite={toggleFavorite}
            onShare={share}
          />
          <CommentSection videoId={video.id} onNotify={(msg) => showToast(msg)} />
        </div>
        <div className="w-full lg:w-[360px] shrink-0">
          <RelatedVideos videoId={video.id} category={video.category} tags={video.tags} />
        </div>
      </div>

      <Toast toast={toast} onClose={hideToast} />
    </div>
  );
}